import { useEffect, useState } from 'react'
import { CalendarRange, CloudRain, ExternalLink, Info, Sprout } from 'lucide-react'
import { label } from './species-names'
import './species-panel.css'


const MONTH_TICKS = [
  [1, 'J'], [32, 'F'], [60, 'M'], [91, 'A'], [121, 'M'], [152, 'J'],
  [182, 'J'], [213, 'A'], [244, 'S'], [274, 'O'], [305, 'N'], [335, 'D'],
]


// Does weather add anything beyond the calendar for this group? Decided by
// the backend's paired bootstrap on identical held-out days, not by eyeballing
// two AUCs that differ by noise.
function verdict(item) {
  const gain = item.validation?.weather_gain_over_season
  const auc = item.validation?.pooled?.roc_auc
  if (!gain || !Number.isFinite(auc)) return null
  const probability = gain.probability_weather_helps
  const lift = gain.auc_gain
  if (probability >= 0.95 && lift > 0) return { kind: 'weather', text: `počasie pridáva k sezóne +${lift.toFixed(2)} AUC (overené)`, auc }
  if (probability >= 0.6) return { kind: 'weak', text: 'počasie pridáva k sezóne len málo', auc }
  return { kind: 'season', text: 'rozhoduje sezóna, počasie navyše nepomáha', auc }
}

const STATE_COPY = {
  waiting_for_rain: 'čaká na nabíjací dážď',
  incubating: 'po daždi · inkubácia',
  fruiting_window: 'plodnicové okno',
  fruiting_window_stressed: 'okno, ale substrát je v strese',
  fading: 'vlna doznieva',
}

const FAILED_COPY = {
  temperature_ready: 'dlhodobá teplota',
  humidity_ready: 'vlhkosť vzduchu',
  water_reserve_ready: '30-dňová vodná rezerva',
  sand_moisture_ready: 'voda v piesku',
  rain_continuity_ready: 'priebežné dažde',
  heat_stress_absent: 'tepelný stres',
}

// The headline is the validated model's index for this time of year; the rain
// sequence explains it but no longer replaces it.
function sequenceValue(day) {
  return day?.index_now ?? day?.chance_percent ?? null
}

// Must match SUITABILITY_BANDS in species_model.py.
function sequenceBand(score) {
  if (score == null) return null
  if (score < 20) return { position: 0, label: 'zlé' }
  if (score < 40) return { position: 1, label: 'slabé' }
  if (score < 65) return { position: 2, label: 'priemerné' }
  if (score < 85) return { position: 3, label: 'dobré' }
  return { position: 4, label: 'výborné' }
}

function SeasonBar({ window: seasonWindow }) {
  const width = 100 / 366
  const left = (seasonWindow.doy_start - 1) * width
  const peak = (seasonWindow.doy_peak - 1) * width
  // A window may wrap the new year (late autumn into January); the model's
  // in_window() handles that, so the bar has to draw it as two segments
  // instead of collapsing to a sliver.
  const wraps = seasonWindow.doy_end < seasonWindow.doy_start
  const segments = wraps
    ? [
      { left, width: (367 - seasonWindow.doy_start) * width },
      { left: 0, width: seasonWindow.doy_end * width },
    ]
    : [{ left, width: Math.max(seasonWindow.doy_end - seasonWindow.doy_start, 1) * width }]
  return (
    <div className="sp-season" role="img"
      aria-label={`Sezóna ${seasonWindow.window}, vrchol ${seasonWindow.peak}`}>
      <div className="sp-season-track">
        {segments.map((segment) => (
          <i className="sp-season-window" key={segment.left}
            style={{ left: `${segment.left}%`, width: `${segment.width}%` }} />
        ))}
        <i className="sp-season-peak" style={{ left: `${peak}%` }} />
      </div>
      <div className="sp-season-ticks" aria-hidden="true">
        {MONTH_TICKS.map(([doy, letter]) => (
          <span key={doy} style={{ left: `${(doy - 1) * width}%` }}>{letter}</span>
        ))}
      </div>
    </div>
  )
}

function ForecastStrip({ forecast, ceiling }) {
  const days = forecast.filter((day) => day.day_offset >= 0).slice(0, 15)
  return (
    <div className="sp-strip" aria-label="Predpoveď na 14 dní">
      {days.map((day) => {
        const value = sequenceValue(day)
        // One shared ceiling across every group, rather than each group scaled
        // to its own maximum: a group peaking at 12 % must not draw the same
        // full bar as one at 90 %, but the shape still needs to be visible.
        const height = value == null ? 4 : Math.max(6, (value / ceiling) * 100)
        return (
          <div key={day.date} className={`sp-bar ${value == null ? 'is-off' : ''}`}
            title={`${day.date}: ${value == null ? 'bez dát' : `${value}/100 · ${STATE_COPY[day.sequence_state] || ''}`}`}>
            <i style={{ height: `${height}%` }} />
          </div>
        )
      })}
    </div>
  )
}

const MONTH_NAMES = ['jan', 'feb', 'mar', 'apr', 'máj', 'jún', 'júl', 'aug', 'sep', 'okt', 'nov', 'dec']

// The running season: hindcast to date, forecast from today, with what people
// actually reported drawn underneath. The hindcast comes from a model trained
// without this year, so the curve is not fitted to the finds it sits above.
function SeasonTrace({ season }) {
  const days = season?.days || []
  if (days.length < 10) return null
  const width = 560
  const height = 132
  const top = 8
  const bottom = 96
  const x = (index) => (index / Math.max(days.length - 1, 1)) * width
  const y = (percent) => bottom - (percent / 100) * (bottom - top)
  const todayIndex = Math.max(days.findIndex((day) => day.forecast), 1) - 1

  // Null chances (out of season) break the line rather than joining across a gap.
  const value = (day) => sequenceValue(day)
  const path = (subset, offset) => {
    const segments = []
    let current = []
    subset.forEach((day, index) => {
      if (value(day) == null) {
        if (current.length > 1) segments.push(current)
        current = []
        return
      }
      current.push(`${x(index + offset).toFixed(1)},${y(value(day)).toFixed(1)}`)
    })
    if (current.length > 1) segments.push(current)
    return segments
  }

  const past = days.slice(0, todayIndex + 1)
  const future = days.slice(todayIndex)
  const areaPoints = path(past, 0)[0]
  const monthStarts = days
    .map((day, index) => ({ index, day: Number(day.date.slice(8, 10)), month: Number(day.date.slice(5, 7)) }))
    .filter((item) => item.day === 1)

  return (
    <div className="sp-trace">
      <svg viewBox={`0 0 ${width} ${height}`} role="img"
        aria-label={`Sekvenčný signál 0 až 100. Sezóna ${season.year}: ${season.found_days} dní s nálezom, predpoveď na ďalších 14 dní`}>
        <text className="sp-trace-axis-title" x={width} y="7" textAnchor="end">
          index pre túto časť sezóny (0–100)
        </text>
        {[[0, '0'], [40, '40 · priemer'], [65, '65 · dobré'], [85, '85 · výborné']].map(([level, name]) => (
          <g className="sp-trace-grid" key={level}>
            <line x1="0" x2={width} y1={y(level)} y2={y(level)} />
            <text x="2" y={y(level) - 3}>{name}</text>
          </g>
        ))}
        {areaPoints && (
          <polygon className="sp-trace-area"
            points={`${x(0)},${bottom} ${areaPoints} ${x(todayIndex)},${bottom}`} />
        )}
        {path(past, 0).map((segment) => (
          <polyline className="sp-trace-line" key={segment[0]} points={segment.join(' ')} />
        ))}
        {path(future, todayIndex).map((segment) => (
          <polyline className="sp-trace-line sp-trace-line--future" key={segment[0]} points={segment.join(' ')} />
        ))}

        {days.map((day, index) => {
          if (!day.reports) return null
          return (
            <line key={day.date} className={`sp-mark ${day.found ? 'is-found' : ''}`}
              x1={x(index)} x2={x(index)} y1={bottom + 6} y2={bottom + 6 + (day.found ? 14 : 7)}>
              <title>{day.date}: {day.reports} hlásení{day.found ? ', nález' : ', bez tejto skupiny'}</title>
            </line>
          )
        })}

        <line className="sp-trace-today" x1={x(todayIndex)} x2={x(todayIndex)} y1={top - 4} y2={bottom + 22} />
        <text className="sp-trace-today-label" x={x(todayIndex)} y={height - 2} textAnchor="middle">dnes</text>
        {monthStarts.map((item) => (
          <text className="sp-trace-month" key={item.index} x={x(item.index)} y={height - 2}>
            {MONTH_NAMES[item.month - 1]}
          </text>
        ))}
      </svg>
      <p className="sp-trace-copy">
        Krivka po dnešok je hindcast modelom, ktorý tento rok nevidel; ďalej beží na predpovedi počasia.
        Sezóna {season.year}: {season.found_days} dní s nálezom z {season.reported_days} dní s hláseniami.
        {season.evidence_until && ` Hlásenia máme do ${season.evidence_until.slice(8)}.${season.evidence_until.slice(5, 7)}.`}
      </p>
    </div>
  )
}

// "37 %" tells a reader almost nothing: for dubák that is a strong day, for
// suchohríby a weak one. The band and the 0-100 index both come from the
// backend, where each day is ranked against the group's own held-out season.
const BANDS = [0, 1, 2, 3, 4]

function BandScale({ index }) {
  const band = sequenceBand(index)
  if (!band) return null
  return (
    <div className="sp-scale" role="img" aria-label={`${band.label}, index ${index} zo 100`}>
      {BANDS.map((position) => (
        <span key={position} className={`sp-scale-step ${position === band.position ? 'is-active' : ''} sp-scale-step--${position}`}>
          {position === band.position ? band.label : ''}
        </span>
      ))}
    </div>
  )
}

function SpeciesCard({ item, ceiling }) {
  const copy = label(item.group)
  const decision = verdict(item)
  const today = item.forecast?.find((day) => day.day_offset === 0)
  const periodRank = (day) => sequenceValue(day)
  const peak = item.forecast?.filter((day) => periodRank(day) != null)
    .reduce((best, day) => (!best || periodRank(day) > periodRank(best) ? day : best), null)
  const currentScore = sequenceValue(today)
  const currentBand = sequenceBand(currentScore)
  const bandClass = currentBand?.position ?? -1
  return (
    <article className={`sp-card sp-card--${decision?.kind || 'none'}`}>
      <header>
        <div>
          <h3>{copy.name}</h3>
          <em>{copy.latin}</em>
        </div>
        <div className={`sp-today sp-today--${bandClass}`}>
          <strong>{currentBand?.label || '—'}</strong>
          <span>{currentScore != null ? `${currentScore}/100` : ''}</span>
        </div>
      </header>

      <BandScale index={currentScore} />
      {currentScore != null && (
        <p className="sp-odds">
          Index {currentScore}/100: {today.band_note || 'v porovnaní s ostatnými dňami tejto časti sezóny'}.
          {today.in_ten_reporting_days != null && ` Model čaká zmienku asi v ${today.in_ten_reporting_days} z 10 dní s hlásením`}
          {today.times_typical_day != null && ` (${String(today.times_typical_day).replace('.', ',')}× bežný deň)`}.
          {today.sequence_state && (
            <>
              {' '}<b>Dažďová sekvencia:</b> {STATE_COPY[today.sequence_state] || today.sequence_state}
              {today.days_since_recharge != null && `, ${today.days_since_recharge}. deň od nabíjacieho dažďa`}.
            </>
          )}
        </p>
      )}

      <SeasonBar window={item.window} />
      <p className="sp-window-copy">
        <CalendarRange size={13} /> Historické jadro nálezov {item.window.window} · medián {item.window.peak}
      </p>

      {item.season?.days?.length
        ? <SeasonTrace season={item.season} />
        : <ForecastStrip forecast={item.forecast} ceiling={ceiling} />}
      <p className="sp-strip-copy">
        14 dní dopredu{peak ? ` · najvyšší signál ${periodRank(peak)}/100 (${peak.date.slice(8)}.${peak.date.slice(5, 7)}.)` : ''}
      </p>

      <dl className="sp-facts">
        <div>
          <dt><CloudRain size={12} /> Lokálne okno po daždi</dt>
          <dd>{item.rule_profile ? `${item.rule_profile.lag_start_days}.–${item.rule_profile.lag_end_days}. deň` : '—'}</dd>
        </div>
        <div>
          <dt>Overené dni</dt>
          <dd>{item.positive_days} / {item.training_days}</dd>
        </div>
        <div>
          <dt>Model · AUC</dt>
          <dd>
            {item.validation?.pooled?.roc_auc?.toFixed(2) || '—'}
            <small> vs. dátum {item.validation?.season_baseline?.roc_auc?.toFixed(2) || '—'}</small>
          </dd>
        </div>
      </dl>

      {decision && <span className={`sp-verdict sp-verdict--${decision.kind}`}>{decision.text}</span>}
      <p className="sp-drivers">Stanovište: {item.habitat || 'regionálny priemer'}.</p>
      {!!today?.sequence_failed_rules?.length && (
        <p className="sp-drivers">Brzdí: {today.sequence_failed_rules.map((name) => FAILED_COPY[name] || name).join(' · ')}</p>
      )}
    </article>
  )
}

export default function SpeciesPanel() {
  const [data, setData] = useState(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    fetch('/species.json')
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error('missing'))))
      .then(setData)
      .catch(() => setFailed(true))
  }, [])

  if (failed || !data) return null
  const modelled = data.species.filter((item) => item.modelled)
  const skipped = data.species.filter((item) => !item.modelled)
  if (!modelled.length) return null
  // Shared vertical scale for every group's 14-day strip.
  const ceiling = Math.max(
    ...modelled.flatMap((item) => item.forecast.map((day) => sequenceValue(day) || 0)),
    10,
  )

  return (
    <section className="sp-panel" id="druhy">
      <div className="section-heading">
        <div>
          <span className="eyebrow"><Sprout size={15} /> Model pre každú skupinu</span>
          <h2>Kedy rastie čo</h2>
        </div>
        <span className="sp-note">{data.source_reports} hlásení · {data.region}</span>
      </div>

      <p className="sp-intro">
        Každá skupina má vlastný aditívny model: sezóna plus počasie posledných
        troch týždňov — dážď a jeho oneskorenie, voda v piesku, noci, vlhkosť a horúčavy.
        Index porovnáva dnešok s dňami tej istej časti sezóny, ktoré model pri učení
        nevidel. Pod ním beží dažďová sekvencia (nabíjací dážď → inkubácia → plodnicové
        okno), ktorá vysvetľuje, v akej fáze vlny sme. Nie je to tvrdenie, že huba v lese je alebo nie je.
      </p>

      <div className="sp-grid">
        {modelled.map((item) => (
          <SpeciesCard item={item} ceiling={ceiling} key={item.group} />
        ))}
      </div>

      {skipped.length > 0 && (
        <p className="sp-skipped">
          <Info size={14} /> Bez modelu (málo dôkazov):{' '}
          {skipped.map((item) => `${label(item.group).name} (${item.positive_days} dní)`).join(', ')}.
        </p>
      )}

      <p className="sp-method">
        Modely sú natrénované z Malackých hlásení a overené vynechaním
        jedného roka ({data.years.join(', ')}); deň v týždni ani história
        hlásení do skóre nevstupujú a „počasie pomáha“ sa rozhoduje párovým
        bootstrapom proti modelu, ktorý pozná iba dátum. Druhy sa určujú z textu hlásení,
        takže „dubáky sme nevideli“ sa nepočíta ako nález.
        Nejde o zaručený nález, ale o podmienky. Neznámu hubu nikdy nejedz.
        {(data.local_evidence || []).map((source) => (
          <a href={source.url} target="_blank" rel="noreferrer" key={source.url}>{source.source} <ExternalLink size={11} /></a>
        ))}
        <a href="#metodika">Metodika <ExternalLink size={11} /></a>
      </p>
    </section>
  )
}
